import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  AlertColor,
  Button,
  Card,
  Box,
  Typography,
  Paper,
  Alert,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LocationOn, Person, TrendingUp, ArrowForward, Close, CheckCircle, ReceiptLong } from '@mui/icons-material';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form from 'components/Form';
import PageLoader from 'components/PageLoader';
import Section from 'components/Section/Section';
import { Dispatch, FC, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import AddEdit from 'screens/Authenticated/CommonPage/AddEdit';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { createDropdownData } from 'utils/validators/HelperFunctions';
import { AddEditBranchInfoForm } from './AddEditBranchInfoForm';
import { CustomerListWithEditModal } from './CustomerListWithEditModal';
import CityPotential from './CityPotential';
import SalesManagerTarget from './SalesManagerTarget';
import CompanyLedgerInfo from '../CompanyLedgerInfo';
import { fetchFromStorage } from 'utils/storage';
import PrimaryButton from 'components/Button/PrimaryButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type BranchInfoProps = {
  companyId: number;
  companyName?: string;
  branchId: number;
  setBranchId: (id: number | null) => void;
  assigneeList: any[];
  countries: any[];
  setIsExpanded: (val: boolean) => void;
  isExpanded: boolean;
  setMessage: Dispatch<
    SetStateAction<{
      display: boolean;
      severity: AlertColor | null;
      message: any;
    }>
  >;
  openDrawer: boolean;
  setOpenDrawer: (val: boolean) => void;
  refreshBranches: () => void;
  setBranches: (branches: any[]) => void;
  branches: any[];
  hideUI?: boolean;
};

const WizardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '90vh',
  backgroundColor: '#f5f5f5',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const Sidebar = styled(Paper)<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  width: isMobile ? '100%' : 320,
  backgroundColor: 'white',
  borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
  borderBottom: isMobile ? '1px solid #e0e0e0' : 'none',
  padding: theme.spacing(3),
  boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    borderRight: 'none',
    borderBottom: '1px solid #e0e0e0',
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

const ContentCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    borderRadius: 4,
  },
}));

const StepIconWrapper = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  '&.completed': {
    backgroundColor: theme.palette.success.main,
    color: 'white',
  },
  '&.active': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  '&.pending': {
    backgroundColor: '#e0e0e0',
    color: '#666',
  },
  [theme.breakpoints.down('sm')]: {
    width: 32,
    height: 32,
    marginRight: theme.spacing(1),
  },
}));

const StepItem = styled(Box)<{ active?: boolean; completed?: boolean }>(({ theme, active, completed }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: active ? '#e3f2fd' : 'transparent',
  border: active ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
  '&:hover': {
    backgroundColor: active ? '#e3f2fd' : '#f5f5f5',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
}));

const MobileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: 'white',
  borderBottom: '1px solid #e0e0e0',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const BranchInfo: FC<BranchInfoProps> = ({
  companyId,
  companyName,
  branchId,
  setBranchId,
  assigneeList,
  countries,
  setMessage,
  openDrawer,
  setOpenDrawer,
  setBranches,
  branches,
  setIsExpanded,
  isExpanded,
  hideUI = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSalesManagementEnabled = fetchFromStorage('isSalesManagementEnabled') === 'true';
  const [cities, setCities] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [locations, setLocations] = useState<any>([]);
  const [cityPotentials, setCityPotentials] = useState<any>([]);
  const [countryPotential, setCountryPotential] = useState<any>({});
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Wizard states
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [openContactsDrawer, setOpenContactsDrawer] = useState<boolean>(false);
  const [openCityPotentialsDrawer, setOpenCityPotentialsDrawer] = useState<boolean>(false);
  const [openSalesManagerDrawer, setOpenSalesManagerDrawer] = useState<boolean>(false);

  let formRef: RefObject<Form | null | undefined> = useRef();
  const rawContacts = Array.isArray(pageResponse?.contacts) ? pageResponse.contacts : [];
  const hasContacts = rawContacts.length > 0 || Number(pageResponse?.contactsCount || 0) > 0;
  const hasBranch = Boolean(pageResponse?.id || branchId);
  const hasCityPotentials = cityPotentials.length > 0;
  const hasTargets = Number(pageResponse?.targetsCount || 0) > 0;
  const steps = [
    {
      id: 'branch',
      title: 'Branch Information',
      description: 'Basic branch details',
      icon: <LocationOn />,
      isCompleted: hasBranch,
      isEnabled: true,
    },
    {
      id: 'contacts',
      title: 'Contact Information',
      description: 'Primary and secondary contacts',
      icon: <Person />,
      isCompleted: hasContacts,
      isEnabled: hasBranch,
    },
    {
      id: 'city-potentials',
      title: 'City Potentials',
      description: 'Market analysis and city data',
      icon: <TrendingUp />,
      isCompleted: hasCityPotentials,
      isEnabled: hasContacts,
    },
    {
      id: 'sales-manager-target',
      title: 'Manager Targets',
      description: 'Manage targets',
      icon: <Person />,
      isCompleted: hasTargets,
      isEnabled: true,
    },
    ...(isSalesManagementEnabled
      ? [
          {
            id: 'ledger',
            title: 'Ledger',
            description: 'Branch settlement ledger',
            icon: <ReceiptLong />,
            isCompleted: false,
            isEnabled: hasBranch,
            isViewOnly: true,
          },
        ]
      : []),
  ];

  const handleCatch = useCallback(
    (error: any) => {
      const message = error.response?.data?.error_description ?? error.response?.data?.message ?? error.message;

      setMessage({
        display: true,
        severity: 'error',
        message: message,
      });
    },
    [setMessage],
  );

  const fetchBranchDetails = useCallback(async () => {
    if (!branchId) return;
    setIsLoading(true);

    try {
      const { status, data } = await axiosInstance.get(URLS.getBranchById(companyId, branchId));
      if (status === 200) {
        setPageResponse({
          ...data,
          secondaryManagers: data?.secondaryManagers,
          country: data?.country?.value,
          state: data?.state?.value,
          city: data?.city?.value,
          location: data?.location?.value,
        });
        setCityPotentials(data?.cityPotentials ?? []);
        setCountryPotential(data?.countryPotential ?? null);

        if (data?.country?.value) {
          // Fetch states for the country
          const statesRes = await axiosInstance.get(URLS.states(data?.country?.value));
          setStates(createDropdownData(statesRes?.data, ['id', 'name']));

          // If state is present, fetch cities for that state
          if (data?.state?.value) {
            const citiesRes = await axiosInstance.get(URLS.cities(data?.state?.value));
            setCities(createDropdownData(citiesRes?.data, ['id', 'name']));
          } else {
            // Fallback to old API if no state
            const res = await axiosInstance.get(URLS.leadCitiesDropdown(data?.country?.value));
            setCities(createDropdownData(res?.data, ['id', 'name']));
          }
        }
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, [branchId, companyId, handleCatch]);

  useEffect(() => {
    fetchBranchDetails();
  }, [fetchBranchDetails]);

  const fetchLocations = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.getLocationList);
      setLocations(createDropdownData(data ?? [], ['id', 'name']));
    } catch (err: any) {
      handleCatch(err);
    }
  }, [handleCatch]);

  useEffect(() => {
    if (openDrawer) {
      fetchLocations();
    }
  }, [openDrawer, fetchLocations]);

  const handleClose = () => {
    formRef.current?.resetForm();

    setOpenDrawer(false);
    setBranchId(null);
    setPageResponse(null);
    setCityPotentials([]);
    setCities([]);
    setStates([]);
    setLocations([]);
    setActiveStep(0);
    setError(null);
  };

  const handleStepClick = (stepIndex: number) => {
    if (steps[stepIndex].isEnabled) {
      setActiveStep(stepIndex);
      setError(null);
    }
  };

  const handleStepComplete = (stepIndex: number) => {
    const updatedSteps = [...steps];
    updatedSteps[stepIndex].isCompleted = true;

    // Enable next step
    if (stepIndex + 1 < steps.length) {
      updatedSteps[stepIndex + 1].isEnabled = true;
    }

    // Move to next step
    if (stepIndex + 1 < steps.length) {
      setActiveStep(stepIndex + 1);
    }
  };

  const handleCustomResponse = (custom: any) => {
    if (!custom) return;
    return {
      ...custom,
      secondaryManagers: (custom.secondaryManagers || [])
        .map((manager: any) => (manager && typeof manager === 'object' ? manager.value : manager))
        .filter((manager: any) => manager !== undefined && manager !== null && manager !== ''),
    };
  };

  const pageConfig = {
    hideClose: true,
    successMessage: strings.common_save_message,
    customMethod: branchId ? 'put' : 'post',
    addEditApiUrl: branchId ? URLS.updateBranchInfo(companyId, branchId) : URLS.addBranchInfo(companyId),
    initialApi: URLS.companyBranchesPage(companyId),
    formModel: AddEditBranchInfoForm(strings, assigneeList, countries, states, cities, locations),
    isCustomResponse: true,
  };

  const onChange = async (field: string, value: any) => {
    if (field === 'country') {
      try {
        setIsLoading(true);
        formRef.current?.handleChange('', 'state', { error: true, message: strings.requiredText });
        formRef.current?.handleChange('', 'city', { error: true, message: strings.requiredText });
        setPageResponse({ ...pageResponse, country: value, state: '', city: '' });

        // Fetch states for the selected country
        const statesRes = await axiosInstance.get(URLS.states(value));
        setStates(createDropdownData(statesRes?.data, ['id', 'name']));
        setCities([]);
      } catch (e) {
        handleCatch(e);
      } finally {
        setIsLoading(false);
      }
    } else if (field === 'state') {
      try {
        setIsLoading(true);
        formRef.current?.handleChange('', 'city', { error: true, message: strings.requiredText });
        setPageResponse({ ...pageResponse, state: value, city: '' });

        // Fetch cities for the selected state
        const citiesRes = await axiosInstance.get(URLS.cities(value));
        setCities(createDropdownData(citiesRes?.data, ['id', 'name']));
      } catch (e) {
        handleCatch(e);
      } finally {
        setIsLoading(false);
      }
    } else if (field === 'location') {
      setPageResponse({ ...pageResponse, location: value });
    } else {
      setPageResponse({ ...pageResponse, [field]: value });
    }
  };

  const handleSave = () => {
    // Check if we're on the last step and all data is filled
    if (activeStep === steps.length - 1) {
      // On the last step, close the modal
      handleClose();
    } else {
      // On other steps, proceed to next step
      handleStepComplete(activeStep);
    }
  };

  const renderStepContent = (stepIndex: number) => {
    const step = steps[stepIndex];

    switch (step.id) {
      case 'branch':
        return (
          <Box sx={{ p: isMobile ? 1 : 2 }}>
            <AddEdit
              handlePropsChange={onChange}
              handleCustomResponse={handleCustomResponse}
              pageConfig={pageConfig}
              pageResponse={pageResponse}
              customCallbackResponse={(data: any) => {
                setBranchId(data?.id);
                const exists = branches.some((branch: any) => branch.id === data.id);
                if (exists) {
                  setBranches(branches.map((branch: any) => (branch.id === data.id ? data : branch)));
                } else {
                  setBranches([...branches, data]);
                }
                handleStepComplete(0);
              }}
            />
          </Box>
        );

      case 'contacts':
        return (
          <Box sx={{ p: isMobile ? 1 : 2 }}>
            <CustomerListWithEditModal
              companyId={companyId}
              companyName={companyName}
              branchId={branchId}
              openDrawer={openContactsDrawer}
              setOpenDrawer={setOpenContactsDrawer}
              onBranchRefresh={fetchBranchDetails}
              branchData={{
                country: pageResponse?.country,
                state: pageResponse?.state,
                city: pageResponse?.city,
                address: pageResponse?.branchAddress,
                states: states,
                cities: cities,
              }}
            />
          </Box>
        );

      case 'city-potentials':
        return (
          <Box sx={{ p: isMobile ? 1 : 2 }}>
            <Typography variant="h6" gutterBottom>
              City Potentials
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Configure market analysis and city data for this branch.
            </Typography>

            <CityPotential
              setMessage={setMessage}
              openDrawer={openCityPotentialsDrawer}
              setOpenDrawer={setOpenCityPotentialsDrawer}
              cityPotentials={cityPotentials}
              setCityPotentials={setCityPotentials}
              cities={cities}
              companyId={companyId}
              branchId={branchId}
              countryPotential={countryPotential}
            />
          </Box>
        );

      case 'sales-manager-target':
        return (
          <Box sx={{ p: isMobile ? 1 : 2 }}>
            <Typography variant="h6" gutterBottom>
              Sales Manager Target
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Manage sales manager targets for this branch.
            </Typography>

            <SalesManagerTarget
              setMessage={setMessage}
              openDrawer={openSalesManagerDrawer}
              setOpenDrawer={setOpenSalesManagerDrawer}
              branchId={branchId}
            />
          </Box>
        );

      case 'ledger':
        return (
          <Box sx={{ p: isMobile ? 1 : 2 }}>
            <Typography variant="h6" gutterBottom>
              {strings.company_master_ledger}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Settlement ledger entries for this branch.
            </Typography>

            <CompanyLedgerInfo companyId={companyId} branchId={branchId} level="branch" />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {!hideUI && <Section />}
      {isLoading && <PageLoader />}

      {!hideUI && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="text" onClick={() => setOpenDrawer(true)}>
            <span style={{ marginRight: '10px' }}>{strings.sales_company_branch_section}</span>
            <AddCircleOutlineIcon />
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </Box>
      )}

      <Collapse in={hideUI ? true : isExpanded}>
        <Box sx={{ mb: 2 }}>
          {openDrawer && (
            <DrawerMenu
              flexColumn={true}
              noPadding={true}
              width={'100vw'}
              open={openDrawer}
              handleClose={handleClose}
              closeButton={true}
              title={strings.sales_company_branch_section}
            >
              <WizardContainer>
                <MobileHeader>
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                      {steps[activeStep].title}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={
                      activeStep === steps.length - 1
                        ? !steps[activeStep].isCompleted
                        : !steps?.[activeStep + 1]?.isEnabled
                    }
                    startIcon={activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />}
                    size={isSmallMobile ? 'small' : 'medium'}
                    sx={{ minWidth: isSmallMobile ? 100 : 140 }}
                  >
                    {activeStep === steps.length - 1 ? 'Complete' : 'Next'}
                  </Button>
                </MobileHeader>

                {/* Sidebar */}
                <Sidebar isMobile={isMobile}>
                  <Box width={'100%'}>
                    <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                      Branch Setup Progress
                    </Typography>
                    {steps.map((step, index) => (
                      <StepItem
                        key={step.id}
                        active={activeStep === index}
                        completed={step.isCompleted}
                        onClick={() => handleStepClick(index)}
                      >
                        <StepIconWrapper
                          className={activeStep === index ? 'active' : step.isCompleted ? 'completed' : 'pending'}
                        >
                          {step.icon}
                        </StepIconWrapper>
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="subtitle2" fontWeight={600}>
                              {step.title}
                            </Typography>
                            {step.isCompleted && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: 'success.main',
                                  ml: 1,
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color={
                              step.isCompleted
                                ? 'success.main'
                                : (step as any).isViewOnly
                                ? 'info.main'
                                : 'text.secondary'
                            }
                            sx={{ fontWeight: step.isCompleted || (step as any).isViewOnly ? 600 : 400 }}
                          >
                            {step.isCompleted
                              ? '✓ Completed'
                              : (step as any).isViewOnly
                              ? (step.isEnabled ? 'View only' : 'Available after saving branch')
                              : step.isEnabled
                              ? 'Ready to start'
                              : 'Waiting for previous step'}
                          </Typography>
                        </Box>
                      </StepItem>
                    ))}

                    {/* Progress Summary */}
                    <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        Progress Summary
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {steps.filter(step => step.isCompleted).length} of {steps.length} steps completed
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          width: '100%',
                          height: 4,
                          backgroundColor: '#e0e0e0',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${(steps.filter(step => step.isCompleted).length / steps.length) * 100}%`,
                            height: '100%',
                            backgroundColor: 'success.main',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="center" mt={2}>
                      <PrimaryButton variant="outlined" onClick={handleClose}>
                        <Close /> Close
                      </PrimaryButton>
                    </Box>
                  </Box>
                </Sidebar>

                {/* Main Content */}
                <MainContent>
                  {!isMobile && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h5" fontWeight={600}>
                        {steps[activeStep].title}
                      </Typography>
                      <Box display="flex" gap={2}>
                        <Button
                          variant="outlined"
                          onClick={handleSave}
                          disabled={
                            activeStep === steps.length - 1
                              ? !steps[activeStep].isCompleted
                              : !steps?.[activeStep + 1]?.isEnabled
                          }
                          endIcon={activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />}
                          sx={{ background: '#fff' }}
                        >
                          {activeStep === steps.length - 1 ? 'Complete' : 'Next'}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {error}
                    </Alert>
                  )}

                  <ContentCard>{renderStepContent(activeStep)}</ContentCard>
                </MainContent>
              </WizardContainer>
            </DrawerMenu>
          )}
        </Box>
      </Collapse>
    </>
  );
};
export default BranchInfo;
